import { useTheme } from '../contexts/ThemeContext'
import { Icon, type IconName } from './ui'

/** One glyph per preference. v3 draws the control icon-only in the rail foot. */
const GLYPH: Record<'light' | 'dark' | 'system', IconName> = {
  light: 'sun',
  dark: 'moon',
  system: 'monitor',
}

/**
 * Light / dark / system, as a three-way group rather than a two-way switch.
 *
 * A bare toggle cannot express "follow the OS", which is the default and the
 * one most people want. `aria-pressed` on each option, not `aria-selected`:
 * these are buttons in a group, not tabs in a tablist.
 *
 * **Icon-only, and still named.** From Sprint 1.9.1 the visible text is a
 * glyph, so the word moves into `.visually-hidden` rather than into a `title`
 * or an `aria-label`: the accessible name stays the same string it always was,
 * the button keeps text content for anything that reads the DOM, and there is
 * no tooltip that a touch device cannot open. `Icon` is `aria-hidden`, so it
 * contributes nothing to the name.
 */
export function ThemeToggle() {
  const { preference, setPreference } = useTheme()

  return (
    <div className="segmented segmented--compact segmented--icons" role="group" aria-label="Theme">
      {(['light', 'dark', 'system'] as const).map((option) => (
        <button
          key={option}
          type="button"
          className="segmented__option"
          aria-pressed={preference === option}
          onClick={() => setPreference(option)}
        >
          <Icon name={GLYPH[option]} />
          <span className="visually-hidden">{option[0].toUpperCase() + option.slice(1)}</span>
        </button>
      ))}
    </div>
  )
}
